// Wraps a static gate detail with live-tick adjustments so the demo
// feels alive: bag scans climb (gates with active downloads), hot
// connection countdowns tick down. Each gate gets a deterministic rate
// so different gates progress at slightly different speeds.

import { useIntel } from './IntelligenceContext';

export function useLiveGateDetail(detail) {
  const { demoTick } = useIntel();
  if (!detail) return detail;

  // Bag scan rate: 0.18–0.42 bags per real second, varies by gate.
  const seed = parseInt(String(detail.gateNum).slice(-2), 10) || 0;
  const rate = 0.18 + (seed % 7) * 0.04;

  const liveScanned = Math.min(
    detail.totalBags,
    detail.scannedBags + Math.floor(demoTick * rate),
  );
  const liveBagPct = detail.totalBags > 0
    ? Math.round((liveScanned / detail.totalBags) * 100)
    : 0;

  // Per-bin scaling — proportional to each bin's outstanding work.
  const totalRemaining = detail.totalBags - detail.scannedBags;
  const totalProgress  = liveScanned - detail.scannedBags;
  const liveBins = detail.bins.map(b => {
    const binRemaining = b.total - b.scanned;
    if (binRemaining <= 0 || totalRemaining <= 0) return b;
    const share = binRemaining / totalRemaining;
    const binAdvance = Math.floor(totalProgress * share);
    return { ...b, scanned: Math.min(b.total, b.scanned + binAdvance) };
  });

  // Connection countdowns drop by elapsed wall-time (in minutes).
  const elapsedMin = Math.floor(demoTick / 60);
  const liveConnections = detail.connections.map(c => ({
    ...c,
    connectionMin: Math.max(0, c.connectionMin - elapsedMin),
  }));

  // Next inbound minutes-away ticks down too.
  const liveNextInbound = detail.nextInbound
    ? { ...detail.nextInbound, minutesAway: Math.max(0, detail.nextInbound.minutesAway - elapsedMin) }
    : null;

  return {
    ...detail,
    scannedBags: liveScanned,
    bagPct: liveBagPct,
    bins: liveBins,
    connections: liveConnections,
    nextInbound: liveNextInbound,
  };
}
